import type { AiChatMessage, AiChatMessageChunk, TransientChunk } from '../common/ai-chat/aiChatTypes';
import { BuildDefinitionStatus, ChatCompletionRequestMessageRole } from '@server/ai';
import type { AgentState } from './agentsChatsTypes';

type ChatDetails = AgentState['chatsDetails'][string];

const attachPendingRunId = (message: AiChatMessage, chatDetails: ChatDetails): AiChatMessage => {
  if (chatDetails.pendingRunId) {
    const messageWithMetadata = {
      ...message,
      runId: chatDetails.pendingRunId,
    };
    chatDetails.pendingRunId = undefined;
    return messageWithMetadata;
  }
  return message;
};

/**
 * Handles artifact metadata chunk - initializes artifact streaming state
 * and creates or marks the message as artifact type
 */
export const handleArtifactMetadata = (
  chunk: AiChatMessageChunk,
  chatDetails: ChatDetails,
  messages: AiChatMessage[],
) => {
  chatDetails.artifactStreamingState = {
    artifactId: chunk.artifactMetadata!.artifactId,
    versionId: chunk.artifactMetadata!.versionId,
    title: chunk.artifactMetadata!.title,
    accumulatedContent: '',
    isComplete: false,
    lastProcessedLength: 0,
    hasCreatedFollowupMessage: false,
    toolResults: chunk.artifactMetadata!.toolResults,
  };

  const lastMessage = messages[messages.length - 1];
  const needsNewMessage =
    messages.length === 0 ||
    !lastMessage ||
    lastMessage.role !== ChatCompletionRequestMessageRole.ASSISTANT;

  if (needsNewMessage) {
    const baseMessage: AiChatMessage = {
      role: ChatCompletionRequestMessageRole.ASSISTANT,
      content: '',
      createdAt: new Date().toISOString(),
      contentType: 'artifact',
      artifactData: {
        artifactId: chunk.artifactMetadata!.artifactId,
        versionId: chunk.artifactMetadata!.versionId,
        title: chunk.artifactMetadata!.title,
      },
    };

    const newMessage = attachPendingRunId(baseMessage, chatDetails);
    messages.push(newMessage);
  } else {
    lastMessage.contentType = 'artifact';
    lastMessage.artifactData = {
      artifactId: chunk.artifactMetadata!.artifactId,
      versionId: chunk.artifactMetadata!.versionId,
      title: chunk.artifactMetadata!.title,
    };
  }
};

/**
 * Handles artifact content chunk - accumulates content and checks for completion
 */
export const handleArtifactContent = (
  chunk: AiChatMessageChunk,
  chatDetails: ChatDetails,
) => {
  if (!chatDetails.artifactStreamingState) return;

  chatDetails.artifactStreamingState.accumulatedContent += chunk.artifactContent!;

  const accumulated = chatDetails.artifactStreamingState.accumulatedContent;
  if (/\n```\s*$/.test(accumulated)) {
    chatDetails.artifactStreamingState.isComplete = true;
  }
};

/**
 * Handles build definition metadata chunk - sets streaming state, initializes the cache entry,
 * and creates an assistant message with contentType 'build_definition'.
 */
export const handleBuildDefinitionMetadata = (
  chunk: AiChatMessageChunk,
  chatDetails: ChatDetails,
  messages: AiChatMessage[],
) => {
  const { buildDefinitionId } = chunk.buildDefinitionMetadata!;

  chatDetails.buildDefinitionStreamingState = { buildDefinitionId };

  if (!chatDetails.buildDefinitions) {
    chatDetails.buildDefinitions = {};
  }
  chatDetails.buildDefinitions[buildDefinitionId] = {
    text: '',
    status: BuildDefinitionStatus.Draft,
  };

  const lastMessage = messages[messages.length - 1];
  const needsNewMessage =
    messages.length === 0 ||
    !lastMessage ||
    lastMessage.role !== ChatCompletionRequestMessageRole.ASSISTANT;

  if (needsNewMessage) {
    const baseMessage: AiChatMessage = {
      role: ChatCompletionRequestMessageRole.ASSISTANT,
      content: '',
      createdAt: new Date().toISOString(),
      contentType: 'build_definition',
      buildDefinitionData: { buildDefinitionId },
    };
    const newMessage = attachPendingRunId(baseMessage, chatDetails);
    messages.push(newMessage);
  } else {
    lastMessage.contentType = 'build_definition';
    lastMessage.buildDefinitionData = { buildDefinitionId };
  }
};

/**
 * Handles build definition content chunk - appends text directly to the cache entry.
 */
export const handleBuildDefinitionContent = (
  chunk: AiChatMessageChunk,
  chatDetails: ChatDetails,
) => {
  if (!chatDetails.buildDefinitionStreamingState || !chatDetails.buildDefinitions) return;

  const { buildDefinitionId } = chatDetails.buildDefinitionStreamingState;
  const entry = chatDetails.buildDefinitions[buildDefinitionId];
  if (entry) {
    entry.text += chunk.buildDefinitionContent!;
  }
};

/**
 * Handles build definition status chunk - updates the status in the cache entry.
 * If the entry doesn't exist (e.g. page reload mid-build stream), bootstraps it
 * so reconnects correctly reflect the current status without dropping the event.
 */
export const handleBuildDefinitionStatus = (
  chunk: AiChatMessageChunk,
  chatDetails: ChatDetails,
) => {
  const { buildDefinitionId, status } = chunk.buildDefinitionStatus!;
  if (!chatDetails.buildDefinitions) {
    chatDetails.buildDefinitions = {};
  }
  if (!chatDetails.buildDefinitions[buildDefinitionId]) {
    chatDetails.buildDefinitions[buildDefinitionId] = { text: '', status: status as BuildDefinitionStatus };
  } else {
    chatDetails.buildDefinitions[buildDefinitionId].status = status as BuildDefinitionStatus;
  }
};

/**
 * Handles the first transient chunk (e.g., tool_explanation).
 *
 * If the last message is already an assistant message with transient chunks,
 * we update it instead of creating a new one. This handles the case where
 * multiple progress updates come via WebSocket with isFirstContentChunk: true
 * (needed to clear loading state) but we don't want duplicate messages.
 */
export const handleFirstTransientChunk = (
  transientChunk: TransientChunk,
  messages: AiChatMessage[],
  chatDetails: ChatDetails,
) => {
  const lastMessage = messages[messages.length - 1];

  if (
    lastMessage?.role === ChatCompletionRequestMessageRole.ASSISTANT &&
    lastMessage.transientChunks
  ) {
    handleSubsequentTransientChunk(transientChunk, messages);
    return;
  }

  const baseMessage: AiChatMessage = {
    role: ChatCompletionRequestMessageRole.ASSISTANT,
    content: '',
    createdAt: new Date().toISOString(),
    transientChunks: [transientChunk],
  };

  const newMessage = attachPendingRunId(baseMessage, chatDetails);
  messages.push(newMessage);
};

/**
 * Handles regular message content with special handling for post-artifact content
 */
export const handleMessageContent = (
  chunk: AiChatMessageChunk,
  chatDetails: ChatDetails,
  messages: AiChatMessage[],
  isFirstContentChunk: boolean,
) => {
  const lastMessage = messages[messages.length - 1];

  if (isFirstContentChunk) {
    const baseMessage: AiChatMessage = {
      ...chunk,
      content: chunk.content!,
      transientChunks: chunk.transientChunk ? [chunk.transientChunk] : undefined,
    };

    const newMessage = attachPendingRunId(baseMessage, chatDetails);
    messages.push(newMessage);
  } else {
    const shouldCreateFollowupMessage =
      chatDetails.artifactStreamingState?.isComplete &&
      !chatDetails.artifactStreamingState.hasCreatedFollowupMessage;

    if (shouldCreateFollowupMessage) {
      const baseMessage: AiChatMessage = {
        role: ChatCompletionRequestMessageRole.ASSISTANT,
        content: chunk.content!,
        createdAt: new Date().toISOString(),
        isError: chunk.isError,
      };

      const newMessage = attachPendingRunId(baseMessage, chatDetails);
      messages.push(newMessage);
      chatDetails.artifactStreamingState!.hasCreatedFollowupMessage = true;
    } else {
      lastMessage.content += chunk.content!;
      lastMessage.isError = chunk.isError;
    }
  }
};

/**
 * Handles transient chunks that come after the first chunk.
 *
 * For tool_explanation chunks, applies special dedup logic:
 * - Consecutive "Research progress:" messages replace the previous one (avoids flooding the UI)
 * - Consecutive "Thinking: " messages append to the previous chunk (streamed partial thoughts
 *   stay in one bullet)
 * Other event types simply push a new entry.
 */
const RESEARCH_PROGRESS_PREFIX = 'Research progress:';
const THINKING_PREFIX = 'Thinking: ';

export const handleSubsequentTransientChunk = (
  transientChunk: TransientChunk,
  messages: AiChatMessage[],
) => {
  const lastMessage = messages[messages.length - 1];
  if (!lastMessage.transientChunks) {
    lastMessage.transientChunks = [];
  }

  if (transientChunk.event === 'tool_explanation') {
    const newData = transientChunk.data;
    const isNewProgressMessage = newData.startsWith(RESEARCH_PROGRESS_PREFIX);
    const isNewThinkingMessage = newData.startsWith(THINKING_PREFIX);

    if ((isNewProgressMessage || isNewThinkingMessage) && lastMessage.transientChunks.length > 0) {
      const lastChunk = lastMessage.transientChunks[lastMessage.transientChunks.length - 1];

      if (lastChunk.event === 'tool_explanation') {
        const isLastProgressMessage = lastChunk.data.startsWith(RESEARCH_PROGRESS_PREFIX);
        const isLastThinkingMessage = lastChunk.data.startsWith(THINKING_PREFIX);

        if (isNewProgressMessage && isLastProgressMessage) {
          lastMessage.transientChunks[lastMessage.transientChunks.length - 1] = {
            event: 'tool_explanation',
            data: newData,
          };
          return;
        }

        if (isNewThinkingMessage && isLastThinkingMessage) {
          lastMessage.transientChunks[lastMessage.transientChunks.length - 1] = {
            event: 'tool_explanation',
            data: lastChunk.data + newData.slice(THINKING_PREFIX.length),
          };
          return;
        }
      }
    }
  }

  lastMessage.transientChunks.push(transientChunk);
};

/**
 * Handles questions-metadata chunk - pushes a new loading-state assistant message.
 * Marks the current last assistant message as having pending questions (loading state).
 * No new message is created — questions are attached to the same message as the streamed text,
 * mirroring how the persistent structure stores them.
 */
export const handleQuestionsMetadataChunk = (
  chatDetails: ChatDetails,
  messages: AiChatMessage[],
) => {
  const lastMessage = messages[messages.length - 1];
  if (lastMessage?.role === ChatCompletionRequestMessageRole.ASSISTANT) {
    lastMessage.contentType = 'questions';
  } else {
    const baseMessage: AiChatMessage = {
      role: ChatCompletionRequestMessageRole.ASSISTANT,
      content: '',
      createdAt: new Date().toISOString(),
      contentType: 'questions',
    };
    messages.push(attachPendingRunId(baseMessage, chatDetails));
  }
};

/**
 * Handles questions chunk - stores questions data on the last assistant message
 */
export const handleQuestionsChunk = (
  chunk: AiChatMessageChunk,
  chatDetails: ChatDetails,
  messages: AiChatMessage[],
) => {
  const lastMessage = messages[messages.length - 1];
  const needsNewMessage =
    messages.length === 0 ||
    !lastMessage ||
    lastMessage.role !== ChatCompletionRequestMessageRole.ASSISTANT;

  if (needsNewMessage) {
    const baseMessage: AiChatMessage = {
      role: ChatCompletionRequestMessageRole.ASSISTANT,
      content: '',
      createdAt: new Date().toISOString(),
      contentType: 'questions',
      questionsData: chunk.questionsData,
    };
    const newMessage = attachPendingRunId(baseMessage, chatDetails);
    messages.push(newMessage);
  } else {
    lastMessage.contentType = 'questions';
    lastMessage.questionsData = chunk.questionsData;
  }
};
